import {
  type HhDataItem,
  hasAITrace,
} from "@inrange/building-manager-api-client";
import { AITraceViewer } from "@inrange/theme-components";
import Markdown from "markdown-to-jsx";
import { Alert, Button, Modal } from "react-bootstrap";

interface DemandFileAnalysisReviewModalProps {
  item: HhDataItem;
  onClose: () => void;
}

// Read-only review of the AI coach analysis for a COMMITTED file. The full
// DemandFileProcessModal is tied to the temp bucket (it offers reprocess actions that
// hit temp-only endpoints), so a committed curve - whose input + attempt history live
// in the permanent bucket - gets this lightweight viewer instead. It renders the same
// explanation + trace the process modal shows, just without any apply/reprocess action.
const DemandFileAnalysisReviewModal = ({
  item,
  onClose,
}: DemandFileAnalysisReviewModalProps) => {
  const analysis = item.latestAnalysis;
  return (
    <Modal show onHide={onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>
          AI file analysis - {item.fileName || item.guid}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {analysis ? (
          <Alert variant="success">
            <Alert.Heading style={{ fontSize: "1rem" }}>
              This curve was auto-fixed with AI
            </Alert.Heading>
            <div
              className="mt-2 ai-analysis-markdown"
              style={{ fontSize: "0.95em" }}
            >
              <style>{`
                .ai-analysis-markdown h1,
                .ai-analysis-markdown h2,
                .ai-analysis-markdown h3 { font-size: 1.05em; font-weight: 600; margin: 0.6em 0 0.2em; }
                .ai-analysis-markdown p { margin: 0 0 0.6em; }
                .ai-analysis-markdown ul, .ai-analysis-markdown ol { margin: 0 0 0.6em; padding-left: 1.5em; }
                .ai-analysis-markdown code { background: rgba(0,0,0,0.06); padding: 0 0.25em; border-radius: 3px; }
                .ai-analysis-markdown pre { background: rgba(0,0,0,0.06); padding: 0.5em; border-radius: 4px; overflow-x: auto; }
                .ai-analysis-markdown pre code { background: transparent; padding: 0; }
              `}</style>
              <Markdown>
                {analysis.details || analysis.proposed_config_rationale || ""}
              </Markdown>
            </div>
            {analysis.proposed_config && (
              <div className="mt-3">
                <div className="text-muted" style={{ fontSize: "0.85em" }}>
                  Config applied
                </div>
                <pre
                  style={{
                    background: "rgba(0,0,0,0.06)",
                    padding: "0.5em",
                    borderRadius: 4,
                    fontSize: "0.8em",
                    marginBottom: 0,
                  }}
                >
                  {JSON.stringify(analysis.proposed_config, null, 2)}
                </pre>
              </div>
            )}
            {hasAITrace(analysis.trace) && (
              <details className="mt-3">
                <summary
                  style={{ cursor: "pointer", fontSize: "0.85em" }}
                  className="text-muted"
                >
                  Show full AI analysis trace ({analysis.trace.entries.length}{" "}
                  steps)
                </summary>
                <div className="mt-2">
                  <AITraceViewer trace={analysis.trace} />
                </div>
              </details>
            )}
          </Alert>
        ) : (
          <p className="text-muted mb-0">
            No AI analysis is available for this file.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DemandFileAnalysisReviewModal;
